import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { checkAdminAuth } from "../lib/adminAuth";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

export default function AdminLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const admin = await checkAdminAuth();
        if (!admin) {
          if (!cancelled) {
            navigate("/admin/login", { replace: true });
          }
          return;
        }

        const res = await fetch(`${API_BASE}/api/admin/leads?limit=100`, { credentials: "include" });
        if (!res.ok) throw new Error("Status " + res.status);
        const json = await res.json();
        if (!cancelled) setLeads(Array.isArray(json.leads) ? json.leads : []);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [navigate]);

  const filtered =
    filter === "all"
      ? leads
      : leads.filter((l) =>
          (l.serviceType || l.service || "").toLowerCase().includes(filter),
        );

  const getLeadSource = (lead) =>
    (lead.source || lead.utm?.source || lead.utm_source || "WEBSITE").toString();

  const getLeadService = (lead) =>
    (lead.serviceType || lead.service || lead.category || "-").toString();

  const getLeadZip = (lead) =>
    (lead.zip || lead.zip_code || lead.contact?.zip || "-").toString();

  const formatLeadDate = (lead) => {
    const raw = lead.created_at || lead.created || lead.inserted_at || "";
    if (!raw) return "-";
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? raw : parsed.toLocaleString();
  };

  const stats = {
    total: leads.length,
    roofing: leads.filter(l => (l.serviceType || "").toLowerCase().includes("roof")).length,
    solar: leads.filter(l => (l.serviceType || "").toLowerCase().includes("solar")).length,
    landscaping: leads.filter(l => (l.serviceType || "").toLowerCase().includes("land")).length,
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Leads Dashboard</h1>
            <p className="text-zinc-400 text-sm mt-1">All incoming homeowner leads</p>
          </div>
          <button onClick={() => window.location.reload()}
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-semibold rounded-lg transition">
            ↻ Refresh
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: "Total Leads", value: stats.total, color: "text-white" },
            { label: "Roofing", value: stats.roofing, color: "text-orange-400" },
            { label: "Solar", value: stats.solar, color: "text-yellow-400" },
            { label: "Landscaping", value: stats.landscaping, color: "text-green-400" },
          ].map(s => (
            <div key={s.label} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
              <p className="text-xs text-zinc-400 uppercase tracking-wide mb-1">{s.label}</p>
              <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {["all", "roof", "solar", "land"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${filter === f ? "bg-orange-600 text-white" : "bg-zinc-800 text-zinc-300 hover:bg-zinc-700"}`}>
              {f === "all" ? "All" : f === "roof" ? "Roofing" : f === "solar" ? "Solar" : "Landscaping"}
            </button>
          ))}
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-950 border border-red-800 text-red-300 rounded-xl px-4 py-3 mb-4 text-sm">
            ⚠️ {error}
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-16 text-zinc-400">
            <div className="text-3xl mb-2 animate-spin">⟳</div>
            Loading leads...
          </div>
        )}

        {/* Empty */}
        {!loading && !error && filtered.length === 0 && (
          <div className="text-center py-16 text-zinc-400">
            <div className="text-4xl mb-3">📭</div>
            <p>No leads yet</p>
            <p className="text-sm mt-1">Share your site to start getting homeowner requests</p>
          </div>
        )}

        {/* Leads Table - Desktop */}
        {!loading && filtered.length > 0 && (
          <>
            {/* Desktop table */}
            <div className="hidden md:block bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-xs uppercase tracking-wide text-zinc-400">
                    <th className="px-4 py-3 text-left">Date</th>
                    <th className="px-4 py-3 text-left">Source</th>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">Phone</th>
                    <th className="px-4 py-3 text-left">Email</th>
                    <th className="px-4 py-3 text-left">Service</th>
                    <th className="px-4 py-3 text-left">ZIP</th>
                    <th className="px-4 py-3 text-left">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((lead, idx) => {
                    const contact = lead.contact || {};
                    const name = contact.name || lead.name || "-";
                    const phone = contact.phone || lead.phone || "-";
                    const email = contact.email || lead.email || "-";
                    const service = getLeadService(lead);
                    const source = getLeadSource(lead);
                    const zip = getLeadZip(lead);
                    const status = lead.status || "new";
                    const date = formatLeadDate(lead);
                    return (
                      <tr key={lead.id || idx} className="border-t border-zinc-800 hover:bg-zinc-800/50 transition">
                        <td className="px-4 py-3 text-zinc-400 whitespace-nowrap">{date}</td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center rounded-full border border-zinc-700 bg-zinc-950 px-2 py-1 text-[11px] font-semibold tracking-wide text-zinc-200">
                            {source}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-white">{name}</td>
                        <td className="px-4 py-3 text-zinc-300">
                          <a href={`tel:${phone}`} className="hover:text-orange-400">{phone}</a>
                        </td>
                        <td className="px-4 py-3 text-zinc-300">
                          <a href={`mailto:${email}`} className="hover:text-orange-400">{email}</a>
                        </td>
                        <td className="px-4 py-3">
                          <span className="bg-orange-500/10 text-orange-400 text-xs px-2 py-1 rounded-full">{service}</span>
                        </td>
                        <td className="px-4 py-3 text-zinc-300">{zip}</td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full ${status === "new" ? "bg-green-500/10 text-green-400" : "bg-zinc-700 text-zinc-300"}`}>
                            {status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden space-y-3">
              {filtered.map((lead, idx) => {
                const contact = lead.contact || {};
                const name = contact.name || lead.name || "-";
                const phone = contact.phone || lead.phone || "-";
                const email = contact.email || lead.email || "-";
                    const service = getLeadService(lead);
                    const source = getLeadSource(lead);
                    const zip = getLeadZip(lead);
                    const date = formatLeadDate(lead);
                    return (
                      <div key={lead.id || idx} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                        <div className="flex items-start justify-between mb-2">
                          <p className="font-semibold text-white">{name}</p>
                          <span className="bg-orange-500/10 text-orange-400 text-xs px-2 py-1 rounded-full">{service}</span>
                        </div>
                        <div className="space-y-1 text-sm text-zinc-300">
                          <p>Source: <span className="text-zinc-100">{source}</span></p>
                          <p>📞 <a href={`tel:${phone}`} className="hover:text-orange-400">{phone}</a></p>
                          <p>✉️ <a href={`mailto:${email}`} className="hover:text-orange-400">{email}</a></p>
                          <p>📍 ZIP {zip} · {date}</p>
                          <p>Status: <span className="text-zinc-100">{lead.status || "new"}</span></p>
                        </div>
                      </div>
                    );
              })}
            </div>

            <p className="text-xs text-zinc-500 mt-4 text-center">
              Showing {filtered.length} of {leads.length} leads
            </p>
          </>
        )}
      </div>
    </div>
  );
}
